import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { CheckCircle, XCircle, Eye, Clock, DollarSign } from 'lucide-react';

const PaymentRequestsManagement = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  useEffect(() => {
    loadRequests();
  }, [filter]);
  
  const loadRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/superadmin/payment-requests?status=${filter}`);
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleApprove = async (requestId) => {
    if (!confirm('¿Aprobar esta solicitud de pago?')) return;
    
    try {
      setProcessing(true);
      await api.put(`/api/superadmin/payment-requests/${requestId}/approve`);
      alert('✅ Solicitud aprobada. Licencia actualizada.');
      await loadRequests();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error aprobando solicitud');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleReject = async (requestId) => {
    const reason = prompt('Razón del rechazo:');
    if (!reason) return;
    
    try {
      setProcessing(true);
      await api.put(`/api/superadmin/payment-requests/${requestId}/reject`, { reason });
      alert('❌ Solicitud rechazada.');
      await loadRequests();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error rechazando solicitud');
    } finally {
      setProcessing(false);
    }
  };
  
  const getRequestTypeLabel = (type) => {
    const labels = {
      first_payment: 'Primer Pago',
      renewal: 'Renovación',
      tools_purchase: 'Herramientas',
      module_addition: 'Módulo Adicional'
    };
    return labels[type] || type;
  };
  
  const getStatusBadge = (status) => {
    const badges = {
      pending: <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">PENDIENTE</span>,
      approved: <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">APROBADO</span>,
      rejected: <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">RECHAZADO</span>
    };
    return badges[status] || status;
  };
  
  return (
    <div className={`rounded-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          📋 Solicitudes de Pago
        </h2>
        
        {/* Filtros */}
        <div className="flex gap-2">
          {['pending', 'approved', 'rejected'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg font-semibold ${
                filter === status
                  ? 'bg-blue-600 text-white'
                  : (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700')
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12">
          <Clock className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
            No hay solicitudes {filter === 'pending' ? 'pendientes' : filter}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((req) => (
            <div
              key={req.id}
              className={`rounded-lg p-4 border-2 ${
                isDark ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {req.user_email}
                    </h3>
                    {getStatusBadge(req.status)}
                  </div>
                  
                  <div className={`text-sm space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <p><span className="font-semibold">Tipo:</span> {getRequestTypeLabel(req.request_type)}</p>
                    <p><span className="font-semibold">Monto:</span> <span className="text-lg font-bold text-blue-600">${req.payment_amount}</span></p>
                    <p><span className="font-semibold">Ciclo:</span> {req.billing_cycle}</p>
                    <p><span className="font-semibold">Fecha:</span> {new Date(req.created_at).toLocaleDateString('es-MX')}</p>
                  </div>
                </div>
                
                {req.status === 'pending' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApprove(req.id)}
                      disabled={processing}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50"
                    >
                      <CheckCircle className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleReject(req.id)}
                      disabled={processing}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentRequestsManagement;
